import {
  Root,
  Trigger,
  Portal,
  Content,
  Item,
  Separator,
  Label,
  type DropdownMenuContentProps,
  type DropdownMenuItemProps,
} from "@radix-ui/react-dropdown-menu";
import * as Ctx from "@radix-ui/react-context-menu";
import {
  type ReactNode,
  forwardRef,
  type ComponentPropsWithoutRef,
} from "react";

import { cn } from "../../lib/cn";
import { ChevronRight } from "./Icon";

/**
 * Themed dropdown menu wrapper. Mirrors the design tokens used by the
 * existing UserMenu so all menus look identical. Supports nested submenus
 * for the kanban "Move to…" pattern and a destructive item variant for
 * permanent delete.
 *
 * Items are label-only (no icon + label pairs, per the design rules).
 *
 * Usage:
 *   <Menu trigger={<IconButton icon={<MoreHorizontal/>} label="More" />}>
 *     <MenuItem onSelect={...}>Edit</MenuItem>
 *     <MenuSeparator />
 *     <MenuItem variant="destructive" onSelect={...}>Delete</MenuItem>
 *   </Menu>
 */

interface MenuProps {
  trigger: ReactNode;
  children: ReactNode;
  align?: DropdownMenuContentProps["align"];
  side?: DropdownMenuContentProps["side"];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

export function Menu({
  trigger,
  children,
  align = "end",
  side = "bottom",
  open,
  onOpenChange,
  className,
}: MenuProps) {
  return (
    <Root open={open} onOpenChange={onOpenChange}>
      <Trigger asChild>{trigger}</Trigger>
      <Portal>
        <Content
          align={align}
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(menuContentClasses, className)}
        >
          {children}
        </Content>
      </Portal>
    </Root>
  );
}

const menuContentClasses = cn(
  "z-50 min-w-[200px] py-1",
  "bg-bg-surface rounded-md border border-border-subtle shadow-md",
  "outline-none",
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
  "motion-reduce:animate-none",
);

interface MenuItemProps
  extends Omit<DropdownMenuItemProps, "asChild" | "children"> {
  shortcut?: string;
  variant?: "default" | "destructive";
  children: ReactNode;
}

export const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(
  ({ shortcut, variant = "default", children, className, ...rest }, ref) => (
    <Item
      ref={ref}
      className={cn(
        "relative flex items-center gap-2 px-3 py-1.5",
        "text-small outline-none cursor-default select-none",
        variant === "default"
          ? "text-text-secondary data-[highlighted]:text-text-primary data-[highlighted]:bg-bg-inset"
          : "text-status-error data-[highlighted]:bg-status-error-soft",
        "data-[disabled]:opacity-40 data-[disabled]:pointer-events-none",
        "transition-colors duration-fast ease-out",
        className,
      )}
      {...rest}
    >
      <span className="flex-1 truncate">{children}</span>
      {shortcut && (
        <span className="text-mono text-text-quaternary">{shortcut}</span>
      )}
    </Item>
  ),
);
MenuItem.displayName = "MenuItem";

export function MenuSeparator() {
  return <Separator className="h-px bg-border-subtle my-1" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <Label className="px-3 py-1.5 text-caption text-text-tertiary">
      {children}
    </Label>
  );
}

/**
 * Optional helper for right-click contexts: render a transparent overlay
 * trigger sized to its parent. The parent component owns positioning.
 *
 * For now we rely on each consumer to call useState + a positioned Root
 * to attach a context menu; we don't need a separate Radix ContextMenu
 * import because the dropdown content shape is identical.
 */
/**
 * ContextMenu — same shape as Menu, but triggered by right-click on any
 * region. The children are rendered as <MenuItem> / <MenuSeparator> just
 * like Menu; we shadow Radix's ContextMenu.Item with our themed Item.
 */

interface ContextMenuProps {
  children: ReactNode; // the region to right-click on
  items: ReactNode; // Item / Separator nodes
}

export function ContextMenu({ children, items }: ContextMenuProps) {
  return (
    <Ctx.Root>
      <Ctx.Trigger asChild>{children}</Ctx.Trigger>
      <Ctx.Portal>
        <Ctx.Content
          collisionPadding={8}
          className={cn(menuContentClasses)}
        >
          {items}
        </Ctx.Content>
      </Ctx.Portal>
    </Ctx.Root>
  );
}

interface CtxItemProps {
  shortcut?: string;
  variant?: "default" | "destructive";
  onSelect?: (event: Event) => void;
  disabled?: boolean;
  title?: string;
  children: ReactNode;
}

export function CtxMenuItem({
  shortcut,
  variant = "default",
  children,
  ...rest
}: CtxItemProps) {
  return (
    <Ctx.Item
      className={cn(
        "relative flex items-center gap-2 px-3 py-1.5",
        "text-small outline-none cursor-default select-none",
        variant === "default"
          ? "text-text-secondary data-[highlighted]:text-text-primary data-[highlighted]:bg-bg-inset"
          : "text-status-error data-[highlighted]:bg-status-error-soft",
        "data-[disabled]:opacity-40 data-[disabled]:pointer-events-none",
        "transition-colors duration-fast ease-out",
      )}
      {...rest}
    >
      <span className="flex-1 truncate">{children}</span>
      {shortcut && (
        <span className="text-mono text-text-quaternary">{shortcut}</span>
      )}
    </Ctx.Item>
  );
}

export function CtxMenuSeparator() {
  return <Ctx.Separator className="h-px bg-border-subtle my-1" />;
}

export function CtxMenuSub({
  trigger,
  children,
}: {
  trigger: ReactNode;
  children: ReactNode;
}) {
  return (
    <Ctx.Sub>
      <Ctx.SubTrigger
        className={cn(
          "relative flex items-center gap-2 px-3 py-1.5",
          "text-small text-text-secondary outline-none cursor-default select-none",
          "data-[highlighted]:text-text-primary data-[highlighted]:bg-bg-inset",
          "data-[state=open]:text-text-primary data-[state=open]:bg-bg-inset",
          "transition-colors duration-fast ease-out",
        )}
      >
        <span className="flex-1 truncate">{trigger}</span>
        <ChevronRight />
      </Ctx.SubTrigger>
      <Ctx.Portal>
        <Ctx.SubContent
          sideOffset={4}
          alignOffset={-4}
          className={menuContentClasses}
        >
          {children}
        </Ctx.SubContent>
      </Ctx.Portal>
    </Ctx.Sub>
  );
}

export type { MenuProps, MenuItemProps };

export const RawTrigger = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<"button">
>((props, ref) => <Trigger ref={ref} asChild {...props} />);
RawTrigger.displayName = "RawTrigger";
